import { useState } from 'react'
import { Settings, Plus, Trash2, Edit, X, Save, GripVertical } from 'lucide-react'
import { Button, LoadingSpinner } from '../components'
import { useMeasurementFieldConfigs } from '../hooks/useMeasurementFieldConfigs'

// ── Category Setup Modal ────────────────────────────────────────────────────
function CategorySetupModal({
  isOpen,
  editingCategory,
  fields,
  setFields,
  isSaving,
  onSave,
  onClose,
}) {
  const [categoryName, setCategoryName] = useState(editingCategory || '')

  const addField = () => {
    setFields((prev) => [
      ...prev,
      { fieldKey: '', label: '', unit: 'inches', sortOrder: prev.length },
    ])
  }

  const removeField = (index) => {
    setFields((prev) => prev.filter((_, i) => i !== index))
  }

  const updateField = (index, key, value) => {
    setFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, [key]: value } : f))
    )
  }

  const handleSave = () => {
    const name = editingCategory || categoryName.trim()
    if (!name) {
      alert('Please enter a garment category name.')
      return
    }
    const valid = fields.every((f) => f.fieldKey.trim() && f.label.trim())
    if (!valid) {
      alert('Every field must have a Field Key and Label.')
      return
    }
    if (fields.length === 0) {
      alert('At least one field is required.')
      return
    }
    const fieldsWithOrder = fields.map((f, i) => ({
      ...f,
      fieldKey: f.fieldKey.trim().toLowerCase().replace(/\s+/g, '_'),
      label: f.label.trim(),
      sortOrder: i,
    }))
    onSave(name, fieldsWithOrder)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <div className="bg-white px-6 pt-5 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-primary-100">
                  <Settings className="h-5 w-5 text-primary-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-secondary-900">
                    {editingCategory ? `Edit Fields — ${editingCategory}` : 'New Garment Category'}
                  </h3>
                  <p className="text-sm text-secondary-500">
                    Define the measurement fields for this garment type
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="text-secondary-400 hover:text-secondary-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Category name (only for new) */}
            {!editingCategory && (
              <div className="mb-4">
                <label className="block text-base font-medium text-secondary-700 mb-1">
                  Garment Category Name *
                </label>
                <input
                  className="input"
                  placeholder="e.g. Shirt, Pant, Blouse, Lehenga"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                />
              </div>
            )}

            {/* Fields table */}
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-secondary-200">
                    <th className="text-left py-2 px-1 text-secondary-600 font-medium w-8">#</th>
                    <th className="text-left py-2 px-2 text-secondary-600 font-medium">Field Key</th>
                    <th className="text-left py-2 px-2 text-secondary-600 font-medium">Label (shown to user)</th>
                    <th className="text-left py-2 px-2 text-secondary-600 font-medium w-28">Unit</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => (
                    <tr key={index} className="border-b border-secondary-100">
                      <td className="py-2 px-1 text-secondary-400">{index + 1}</td>
                      <td className="py-2 px-2">
                        <input
                          className="input text-sm"
                          placeholder="e.g. chest, waist"
                          value={field.fieldKey}
                          onChange={(e) => updateField(index, 'fieldKey', e.target.value)}
                        />
                      </td>
                      <td className="py-2 px-2">
                        <input
                          className="input text-sm"
                          placeholder="e.g. Chest / Seena"
                          value={field.label}
                          onChange={(e) => updateField(index, 'label', e.target.value)}
                        />
                      </td>
                      <td className="py-2 px-2">
                        <select
                          className="input text-sm"
                          value={field.unit}
                          onChange={(e) => updateField(index, 'unit', e.target.value)}
                        >
                          <option value="inches">inches</option>
                          <option value="cm">cm</option>
                        </select>
                      </td>
                      <td className="py-2 px-1">
                        <button
                          type="button"
                          onClick={() => removeField(index)}
                          className="text-danger-500 hover:text-danger-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fields.length === 0 && (
                <p className="text-center text-secondary-400 py-6 text-sm">
                  No fields yet. Click "Add Field" to start.
                </p>
              )}
            </div>

            {/* Add field button */}
            <button
              type="button"
              onClick={addField}
              className="mt-3 flex items-center text-sm text-primary-600 hover:text-primary-800 font-medium"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Field
            </button>
          </div>

          {/* Footer */}
          <div className="bg-secondary-50 px-6 py-3 flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleSave} loading={isSaving} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Fields'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main Page ───────────────────────────────────────────────────────────────
function MeasurementSetup() {
  const {
    categories,
    isLoading,
    showModal,
    editingCategory,
    categoryFields,
    setCategoryFields,
    isSaving,
    isDeleting,
    handleOpenSetup,
    handleSaveCategory,
    handleDeleteCategory,
    handleCloseModal,
  } = useMeasurementFieldConfigs()

  if (isLoading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Measurement Setup</h1>
          <p className="text-secondary-500 mt-1">
            Configure measurement fields for each garment category your company uses.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => handleOpenSetup(null)}
          className="flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Category
        </Button>
      </div>

      {/* Categories grid */}
      {categories.length === 0 ? (
        <div className="bg-white rounded-lg border-2 border-dashed border-secondary-300 p-16 text-center">
          <Settings className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 mb-2">No garment categories yet</h3>
          <p className="text-secondary-500 mb-6 max-w-sm mx-auto">
            Add your first garment category (e.g. Shirt, Pant, Blouse) and define its measurement fields.
          </p>
          <Button variant="primary" onClick={() => handleOpenSetup(null)}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Category
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div
              key={category}
              className="bg-white rounded-lg border border-secondary-200 p-5 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow"
            >
              <div>
                <div className="flex items-center mb-3">
                  <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                    <Settings className="h-5 w-5 text-primary-600" />
                  </div>
                  <h3 className="text-base font-semibold text-secondary-900 capitalize">
                    {category}
                  </h3>
                </div>
              </div>
              <div className="flex space-x-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 flex items-center justify-center"
                  onClick={() => handleOpenSetup(category)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit Fields
                </Button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => handleDeleteCategory(category)}
                  className="p-2 rounded-md border border-danger-200 text-danger-500 hover:bg-danger-50 hover:text-danger-700 transition-colors disabled:opacity-50"
                  title="Delete category"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Setup modal */}
      <CategorySetupModal
        isOpen={showModal}
        editingCategory={editingCategory}
        fields={categoryFields}
        setFields={setCategoryFields}
        isSaving={isSaving}
        onSave={handleSaveCategory}
        onClose={handleCloseModal}
      />
    </div>
  )
}

export default MeasurementSetup
